import { useEffect, useRef, useState } from 'react';
import { Camera, CameraOff, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n/useT';

/**
 * Caso 11 v2 — Camera-based QR scanner for the booth-operator redemption
 * flow. Uses the browser-native `BarcodeDetector` API (Chromium-based +
 * Safari 17+) so we don't ship a 200 KB qr library for the only screen that
 * needs it.
 *
 * Hosts pass `onCode(code)` and the scanner extracts a normalized
 * redemption code from anything it scans:
 *  - Raw code string (e.g. "X4P9-MN2K") → returned as-is.
 *  - Onxite URL with `?code=...` (e.g. printed QR sticker) → query value.
 *
 * Falls back to a "not supported" message when the browser lacks
 * `BarcodeDetector` — the manual input on the parent page remains the
 * primary flow.
 */
export function QrCodeScanner({
  open,
  onClose,
  onCode,
}: {
  open: boolean;
  onClose: () => void;
  onCode: (code: string) => void;
}) {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState<boolean | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);

    // Use `any` to avoid pulling a custom type for an experimental API.
    const Ctor = (globalThis as any).BarcodeDetector;
    if (!Ctor) {
      setSupported(false);
      return;
    }
    setSupported(true);

    let stream: MediaStream | null = null;
    let raf = 0;
    let cancelled = false;
    const detector = new Ctor({ formats: ['qr_code'] });

    async function start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (cancelled) return;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        scan();
      } catch (e: any) {
        setError(e?.message ?? t('operator.scanner.cameraError'));
      }
    }

    async function scan() {
      const video = videoRef.current;
      if (!video || cancelled) return;
      try {
        const codes = await detector.detect(video);
        if (codes && codes[0]?.rawValue) {
          const extracted = extractCode(codes[0].rawValue);
          if (extracted) {
            onCode(extracted);
            return;
          }
        }
      } catch {
        // Detector throws on frames it can't process — that's expected;
        // keep polling.
      }
      raf = requestAnimationFrame(scan);
    }

    start();

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (stream) stream.getTracks().forEach((tr) => tr.stop());
    };
  }, [open, onCode, t]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="qr-scanner-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4 shadow-2xl">
        <header className="mb-3 flex items-center justify-between">
          <h2
            id="qr-scanner-title"
            className="flex items-center gap-2 text-base font-semibold"
          >
            <Camera className="h-4 w-4" />
            {t('operator.scanner.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded p-1 hover:bg-[var(--color-bg-elevated)]"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {supported === false ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CameraOff className="h-10 w-10 text-[var(--color-fg-subtle)]" />
            <p className="text-sm text-[var(--color-fg-muted)]">
              {t('operator.scanner.unsupported')}
            </p>
            <Button variant="ghost" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        ) : (
          <>
            <div className="relative overflow-hidden rounded-lg bg-black">
              <video
                ref={videoRef}
                playsInline
                muted
                className="aspect-square w-full object-cover"
              />
              {/* Centred crosshair to help the operator aim. */}
              <div className="pointer-events-none absolute inset-8 rounded-lg border-2 border-white/60" />
            </div>
            {error && (
              <p className="mt-2 text-xs text-[var(--color-error)]">{error}</p>
            )}
            <p className="mt-2 text-center text-xs text-[var(--color-fg-muted)]">
              {t('operator.scanner.hint')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Extract a redemption code from the raw QR payload. Handles both bare
 * codes and Onxite URLs with `?code=` query string.
 */
export function extractCode(raw: string): string | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return null;
  // URL form — pull `code` query param.
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const u = new URL(trimmed);
      const c = u.searchParams.get('code');
      if (c) return c.toUpperCase();
    } catch {
      return null;
    }
  }
  // Bare code form — normalise to upper case and strip stray whitespace.
  return trimmed.toUpperCase();
}
